import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { AgentProfile } from '@Types/agent';
import { Link } from 'react-router-dom';
import AgentCarouselCard from './agentCarouselCard';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { useAgentProfiles } from '@Hooks/useAgentProfile';

export default function AgentCarousel({
  setCurrentAgent,
}: {
  setCurrentAgent: Dispatch<SetStateAction<AgentProfile | null>>;
}) {
  const [index, setIndex] = useState(0);

  const { data: allProfiles, status: profilesStatus } = useAgentProfiles({});
  const profiles = allProfiles?.length
    ? [
        ...allProfiles.filter((p) => p.isDefault),
        ...allProfiles.filter((p) => !p.isDefault),
      ]
    : [];
  const total = profiles?.length ?? 0;

  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);

  const profile = profiles?.[index];

  // Ai Chat Bar agent set to default agent
  useEffect(() => {
    if (profiles?.length) {
      const defaultProfile = profiles?.find((p) => p.isDefault === true);
      setCurrentAgent(defaultProfile);
    }
  }, [profiles]);

  // Ai Chat Bar agent set by carousel
  // useEffect(() => {
  //   if (profiles?.length && profiles[index]) {
  //     setCurrentAgent(profiles[index]);
  //   }
  // }, [index, profiles]);

  if (profilesStatus !== 'success') {
    return (
      <Card className="col-span-2 flex h-75 items-center justify-center rounded-xl">
        <div className="h-6 w-6 animate-spin rounded-full border-5 border-zinc-200 border-t-[#CAAB6A]" />
      </Card>
    );
  }

  if (!profile) return null;

  return (
    <Card className="relative col-span-2 flex h-75 flex-col justify-center rounded-xl p-4">
      <CardContent>
        <Link to={`/agents/${profile.id}`}>
          <AgentCarouselCard profile={profile} />
        </Link>

        {total > 1 && (
          <div className="mt-3 flex items-center justify-center gap-5">
            <div onClick={prev}>
              <ChevronLeft className="h-6 w-6" />
            </div>

            <div className="flex items-center gap-1.5">
              {profiles.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  className={`h-2 rounded-full transition-all duration-200 ${
                    i === index
                      ? 'w-4 bg-zinc-700'
                      : 'w-2 bg-zinc-400 hover:bg-zinc-500'
                  }`}
                  aria-label={`Go to profile ${i + 1}`}
                />
              ))}
            </div>

            <div onClick={next}>
              <ChevronRight className="h-6 w-6" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
